import m from 'mithril';
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Link from 'flarum/common/components/Link';
import avatar from 'flarum/common/helpers/avatar';
import Post from 'flarum/forum/components/Post';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import { rankOfAttrs } from './rank';

// Rank dari data asli: badge fof/badges (God/Expert/Intermediate/Newbie),
// lalu grup staff (Admin id 1 / Mod id 4), fallback jumlah post.
function badgeNames(user) {
  const out = [];
  try {
    const primary = user.attribute ? user.attribute('primaryBadgeName') : null;
    if (primary) out.push(String(primary).toLowerCase());
  } catch (_) {
    // abaikan
  }
  try {
    const rel = user.badges && user.badges();
    const list = rel && rel.toArray ? rel.toArray() : rel || [];
    list.forEach((b) => {
      try {
        out.push(String(b.name ? b.name() : b.attribute('name')).toLowerCase());
      } catch (_) {
        // abaikan
      }
    });
  } catch (_) {
    // abaikan
  }
  return out;
}

function groupIds(user) {
  try {
    const rel = user.groups && user.groups();
    const list = Array.isArray(rel) ? rel : rel && rel.toArray ? rel.toArray() : [];
    return list.map((g) => {
      try {
        return String(g.id());
      } catch (_) {
        return '';
      }
    });
  } catch (_) {
    return [];
  }
}

function rankOf(user) {
  if (!user) return { label: 'DELETED', title: 'Deleted', cls: 'is-deleted', staff: null, icon: '' };
  let commentCount = 0;
  try {
    commentCount = user.commentCount() || 0;
  } catch (_) {
    commentCount = 0;
  }
  return rankOfAttrs({ commentCount }, { badges: badgeNames(user), groups: groupIds(user) });
}

function joinedLabel(user) {
  try {
    const d = user.joinTime && user.joinTime();
    if (d) return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch (_) {
    // abaikan
  }
  return '-';
}

function isOnline(user) {
  try {
    const d = user.lastSeenAt && user.lastSeenAt();
    return !!d && Date.now() - d.getTime() < 5 * 60 * 1000;
  } catch (_) {
    return false;
  }
}

function intFmt(n) {
  return (n || 0).toLocaleString('en-US');
}

// Panel user kiri gaya BreachForums: nama+rank, avatar kotak, kotak rank, stats.
const RvsrUserbit = {
  view(vnode) {
    const post = vnode.attrs.post;
    const user = post.user();
    if (!user) {
      return (
        <aside className="RvsrUserbit is-deleted">
          <div className="RvsrUserbit-name">Deleted</div>
        </aside>
      );
    }
    const rank = rankOf(user);
    const rankCls = rank.cls + (rank.staff === 'Owner' ? ' is-admin' : rank.staff === 'Staff' ? ' is-mod' : '');
    let threads = 0;
    let posts = 0;
    try {
      threads = user.discussionCount() || 0;
    } catch (_) {
      threads = 0;
    }
    try {
      posts = user.commentCount() || 0;
    } catch (_) {
      posts = 0;
    }
    const online = isOnline(user);
    return (
      <aside className={'RvsrUserbit ' + rankCls}>
        <Link className="RvsrUserbit-name" href={app.route.user(user)}>
          {rank.staff ? <span className="RvsrUserbit-staff">[{rank.staff}]</span> : null}
          {user.displayName ? user.displayName() : user.username()}
        </Link>
        <Link className="RvsrUserbit-avatar" href={app.route.user(user)}>
          {avatar(user, { className: 'RvsrUserbit-avatarImg', loading: 'lazy' })}
        </Link>
        <div className="RvsrUserbit-title">
          {rank.title}
          <span className={'RvsrUserbit-dot' + (online ? ' is-on' : '')} title={online ? 'Online' : 'Offline'} />
        </div>
        <div className="RvsrUserbit-rank">
          {rank.icon ? <img className="RvsrUserbit-rankIcon" src={'/assets/brand/rank/' + rank.icon + '.png'} alt="" loading="lazy" /> : null}
          {rank.label}
        </div>
        <dl className="RvsrUserbit-stats">
          <div>
            <dt>Posts</dt>
            <dd>{intFmt(posts)}</dd>
          </div>
          <div>
            <dt>Threads</dt>
            <dd>{intFmt(threads)}</dd>
          </div>
          <div>
            <dt>Joined</dt>
            <dd>{joinedLabel(user)}</dd>
          </div>
        </dl>
      </aside>
    );
  },
};

// Userbit masuk via viewItems (vdom-native) biar selamat dari redraw/scrubber.
function addUserbit(items) {
  if (!(this instanceof CommentPost)) return;
  // hanya di halaman diskusi — di profile/search, post pakai layout bawaan
  // (kalau tidak, identitas dobel: userbit + header post)
  try {
    if (!app.current || !app.current.matches || !app.current.matches(DiscussionPage)) return;
  } catch (_) {
    return;
  }
  const post = this.attrs.post;
  if (!post) return;
  items.add('rvsr-userbit', <RvsrUserbit post={post} />, 110);
}

function heroDate(d) {
  try {
    const s = d.toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return s.replace(/, (\d{1,2}:)/, ' at $1');
  } catch (_) {
    return '';
  }
}

export default function addPostbit() {
  // DiscussionHero: bar judul + "by author - date".
  extend(DiscussionHero.prototype, 'items', function (items) {
    const discussion = this.attrs.discussion;
    if (!discussion) return;
    let author = null;
    try {
      author = discussion.user ? discussion.user() : null;
    } catch (_) {
      author = null;
    }
    let created = null;
    try {
      created = discussion.createdAt ? discussion.createdAt() : null;
    } catch (_) {
      created = null;
    }
    items.add(
      'rvsr-byline',
      <div className="DiscussionHero-byline">
        by{' '}
        {author ? <Link href={app.route.user(author)}>{author.displayName ? author.displayName() : author.username()}</Link> : 'Unknown'}
        {created ? ` - ${heroDate(created)}` : ''}
      </div>,
      50
    );
  });

  // Panel userbit kiri tiap post.
  extend(Post.prototype, 'viewItems', function (items) {
    addUserbit.call(this, items);
  });

  // Chip rank ML di header post (ganti chip fof-badges generic).
  extend(CommentPost.prototype, 'headerItems', function (items) {
    const post = this.attrs.post;
    let user = null;
    try {
      user = post && post.user ? post.user() : null;
    } catch (_) {
      user = null;
    }
    if (!user) return;
    const rank = rankOf(user);
    if (!rank || !rank.icon) return;
    const rankCls = rank.cls + (rank.staff === 'Owner' ? ' is-admin' : rank.staff === 'Staff' ? ' is-mod' : '');
    items.add(
      'rvsr-rank-chip',
      <span className={'RvsrRankChip ' + rankCls}>
        <img src={'/assets/brand/rank/' + rank.icon + '.png'} alt="" loading="lazy" />
        {rank.label}
      </span>,
      10
    );
  });
}
